import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../i18n";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  createProductBarcode,
  deleteProductBarcode,
  getProductBarcodes,
  setPrimaryProductBarcode,
} from "../api/products";
import { getApiError } from "../api/errors";

const emptyForm = {
  code: "",
  symbology: "",
  is_primary: false,
};

export default function ProductBarcodeDialog({ open, product, variant = null, onClose }) {
  const { t } = useTranslation();
  const [barcodes, setBarcodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [primaryId, setPrimaryId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState(emptyForm);
  const codeInputRef = useRef(null);

  const productPublicId = product?.public_id || "";

  const load = useCallback(async () => {
    if (!productPublicId) return;
    setLoading(true);
    setError("");
    try {
      const response = await getProductBarcodes(productPublicId);
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      setBarcodes(items.filter((item) =>
        (item.product_variant_public_id || null) === (variant?.public_id || null)));
    } catch (requestError) {
      setError(getApiError(requestError, t("barcodes.loadError")));
    } finally {
      setLoading(false);
    }
  }, [productPublicId, variant?.public_id, t]);

  useEffect(() => {
    if (!open || !productPublicId) return;
    setForm(emptyForm);
    setSuccess("");
    setError("");
    load();
  }, [load, open, productPublicId]);

  useEffect(() => {
    if (!open || loading) return;
    const timer = window.setTimeout(() => codeInputRef.current?.focus(), 80);
    return () => window.clearTimeout(timer);
  }, [open, loading, barcodes.length]);

  const addBarcode = async () => {
    const code = form.code.trim();
    if (!code) {
      setError(t("barcodes.enterFirst"));
      codeInputRef.current?.focus();
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await createProductBarcode(productPublicId, {
        code,
        symbology: form.symbology.trim() || null,
        is_primary: Boolean(form.is_primary),
        product_variant_public_id: variant?.public_id || null,
      });
      setForm(emptyForm);
      setSuccess(t("barcodes.assignedSuccess"));
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, t("barcodes.assignError")));
    } finally {
      setSaving(false);
    }
  };

  const makePrimary = async (barcode) => {
    if (barcode.is_primary) return;
    setPrimaryId(barcode.public_id);
    setError("");
    setSuccess("");
    try {
      await setPrimaryProductBarcode(productPublicId, barcode.public_id);
      setSuccess(t("barcodes.updatedPrimary"));
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, t("barcodes.primaryError")));
    } finally {
      setPrimaryId("");
    }
  };

  const removeBarcode = async (barcode) => {
    if (!window.confirm(t("barcodes.removeConfirm", { code: barcode.code, name: product?.name || t("barcodes.thisProduct") }))) return;
    setDeletingId(barcode.public_id);
    setError("");
    setSuccess("");
    try {
      await deleteProductBarcode(productPublicId, barcode.public_id);
      setSuccess(t("barcodes.removed"));
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, t("barcodes.removeError")));
    } finally {
      setDeletingId("");
    }
  };

  const busy = saving || Boolean(deletingId) || Boolean(primaryId);

  return (
    <Dialog open={open} onClose={() => { if (!busy) onClose?.(); }} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1.25 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              display: "grid",
              placeItems: "center",
              borderRadius: 2.5,
              bgcolor: "#eff6ff",
              color: "primary.main",
              border: "1px solid #dbeafe",
            }}
          >
            <QrCodeScannerRoundedIcon />
          </Box>
          <Box>
            <Typography component="div" variant="h6" fontWeight={900}>{t(variant ? "barcodes.variantTitle" : "barcodes.productTitle")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {product?.brand} {product?.name}{variant?.label ? ` · ${variant.label}` : ""}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2.25 }}>
          {t("barcodes.help")}
        </Alert>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

        <Box
          sx={{
            p: 2,
            border: "1px solid #e2e8f0",
            borderRadius: 3,
            bgcolor: "#f8fafc",
          }}
        >
          <Typography fontWeight={900} sx={{ mb: 1.5 }}>{t("barcodes.assign")}</Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "flex-start" }}>
            <TextField
              inputRef={codeInputRef}
              fullWidth
              label={t("barcodes.barcode")}
              placeholder={t("barcodes.placeholder")}
              value={form.code}
              disabled={saving}
              autoComplete="off"
              onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !saving) {
                  event.preventDefault();
                  addBarcode();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCodeScannerRoundedIcon fontSize="small" sx={{ color: "#64748b" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1.7 }}
            />
            <TextField
              fullWidth
              label={t("barcodes.symbology")}
              placeholder={t("barcodes.scannerFormat")}
              value={form.symbology}
              disabled={saving}
              autoComplete="off"
              inputProps={{ maxLength: 40 }}
              onChange={(event) => setForm((current) => ({ ...current, symbology: event.target.value }))}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <AddRoundedIcon />}
              onClick={addBarcode}
              disabled={saving || !form.code.trim()}
              sx={{ minHeight: 56, px: 2.25, whiteSpace: "nowrap" }}
            >
              {t(saving ? "barcodes.adding" : "barcodes.add")}
            </Button>
          </Stack>
          <FormControlLabel
            sx={{ mt: 0.6 }}
            control={(
              <Checkbox
                checked={form.is_primary}
                disabled={saving}
                onChange={(event) => setForm((current) => ({ ...current, is_primary: event.target.checked }))}
              />
            )}
            label={t("barcodes.primaryHelp")}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {t("barcodes.firstPrimary", { type: t(variant ? "common.variant" : "common.product").toLowerCase() })}
          </Typography>
        </Box>

        <Divider sx={{ my: 2.5 }} />

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
          <Typography fontWeight={900}>{t("barcodes.assigned")}</Typography>
          <Chip label={t("barcodes.assignedCount", { count: barcodes.length })} size="small" variant="outlined" />
        </Stack>

        {loading ? (
          <Box sx={{ minHeight: 150, display: "grid", placeItems: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : barcodes.length === 0 ? (
          <Box
            sx={{
              minHeight: 130,
              display: "grid",
              placeItems: "center",
              textAlign: "center",
              p: 2,
              border: "1px dashed #cbd5e1",
              borderRadius: 3,
            }}
          >
            <Box>
              <Typography fontWeight={850}>{t("barcodes.none")}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                {t("barcodes.noneHelp")}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Stack spacing={1}>
            {barcodes.map((barcode) => (
              <Box
                key={barcode.public_id}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  border: "1px solid #e2e8f0",
                  borderRadius: 2.5,
                  bgcolor: barcode.is_primary ? "#f8fbff" : "#fff",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography
                      component="code"
                      sx={{
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                        fontWeight: 850,
                        wordBreak: "break-all",
                      }}
                    >
                      {barcode.code}
                    </Typography>
                    {barcode.is_primary && <Chip label={t("barcodes.primary")} size="small" color="primary" />}
                    {barcode.symbology && <Chip label={barcode.symbology} size="small" variant="outlined" />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {t("barcodes.exactIdentity")}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title={t(barcode.is_primary ? "barcodes.primaryBarcode" : "barcodes.setPrimary")}>
                    <span>
                      <IconButton
                        color={barcode.is_primary ? "primary" : "default"}
                        disabled={busy || barcode.is_primary}
                        onClick={() => makePrimary(barcode)}
                        aria-label={t(barcode.is_primary ? "barcodes.primaryBarcode" : "barcodes.setPrimaryAria")}
                      >
                        {primaryId === barcode.public_id ? (
                          <CircularProgress size={21} />
                        ) : barcode.is_primary ? (
                          <StarRoundedIcon />
                        ) : (
                          <StarBorderRoundedIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t("barcodes.remove")}>
                    <span>
                      <IconButton
                        color="error"
                        disabled={busy}
                        onClick={() => removeBarcode(barcode)}
                        aria-label={t("barcodes.remove")}
                      >
                        {deletingId === barcode.public_id ? (
                          <CircularProgress size={21} />
                        ) : (
                          <DeleteOutlineRoundedIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={busy}>{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
